import { z } from 'zod';
export const customUiCspDirectives = Object.freeze(['scriptSrc', 'connectSrc']);
export const customUiCspGuard = z
    .object({
    scriptSrc: z.string().array().optional(),
    connectSrc: z.string().array().optional(),
})
    .strict();
export var CustomUiCspSourceValidationErrorCode;
(function (CustomUiCspSourceValidationErrorCode) {
    CustomUiCspSourceValidationErrorCode["EmptySource"] = "empty_source";
    CustomUiCspSourceValidationErrorCode["SemicolonNotAllowed"] = "semicolon_not_allowed";
    CustomUiCspSourceValidationErrorCode["CspKeywordNotSupported"] = "csp_keyword_not_supported";
    CustomUiCspSourceValidationErrorCode["MalformedUrl"] = "malformed_url";
    CustomUiCspSourceValidationErrorCode["DisallowedUrlParts"] = "disallowed_url_parts";
    CustomUiCspSourceValidationErrorCode["UnsupportedScheme"] = "unsupported_scheme";
    CustomUiCspSourceValidationErrorCode["MalformedWildcardHost"] = "malformed_wildcard_host";
    CustomUiCspSourceValidationErrorCode["MalformedHost"] = "malformed_host";
})(CustomUiCspSourceValidationErrorCode || (CustomUiCspSourceValidationErrorCode = {}));
const validHostLabelRegEx = /^[\da-z](?:[\da-z-]{0,61}[\da-z])?$/;
const isValidHostLabel = (label) => validHostLabelRegEx.test(label);
const validateHostname = (hostname) => {
    if (hostname === 'localhost') {
        return;
    }
    const labels = hostname.split('.');
    const wildcardCount = labels.filter((label) => label === '*').length;
    if (wildcardCount > 0) {
        return labels[0] === '*' &&
            wildcardCount === 1 &&
            labels.length >= 3 &&
            labels.slice(1).every((label) => isValidHostLabel(label))
            ? undefined
            : CustomUiCspSourceValidationErrorCode.MalformedWildcardHost;
    }
    return labels.length >= 2 && labels.every((label) => isValidHostLabel(label))
        ? undefined
        : CustomUiCspSourceValidationErrorCode.MalformedHost;
};
const validateScheme = (directive, url, { isProduction = false }) => {
    const isLocalhostHttpSource = !isProduction && url.protocol === 'http:' && url.hostname === 'localhost' && url.port;
    if (isLocalhostHttpSource) {
        return;
    }
    const allowedSchemes = directive === 'connectSrc' ? ['https:', 'wss:'] : ['https:'];
    return allowedSchemes.includes(url.protocol)
        ? undefined
        : CustomUiCspSourceValidationErrorCode.UnsupportedScheme;
};
const getInvalidPlainSourceCode = (source) => {
    if (!source) {
        return CustomUiCspSourceValidationErrorCode.EmptySource;
    }
    if (source.includes(';')) {
        return CustomUiCspSourceValidationErrorCode.SemicolonNotAllowed;
    }
    return source.includes("'")
        ? CustomUiCspSourceValidationErrorCode.CspKeywordNotSupported
        : undefined;
};
const hasDisallowedUrlParts = (url) => Boolean(url.username || url.password || url.search || url.hash);
const normalizeParsedSourceExpression = (directive, url, options) => {
    if (hasDisallowedUrlParts(url)) {
        return {
            isValid: false,
            code: CustomUiCspSourceValidationErrorCode.DisallowedUrlParts,
        };
    }
    const invalidSchemeCode = validateScheme(directive, url, options ?? {});
    if (invalidSchemeCode) {
        return { isValid: false, code: invalidSchemeCode };
    }
    const invalidHostCode = validateHostname(url.hostname);
    if (invalidHostCode) {
        return { isValid: false, code: invalidHostCode };
    }
    return {
        isValid: true,
        value: `${url.protocol}//${url.host}${url.pathname === '/' ? '' : url.pathname}`,
    };
};
export const normalizeCustomUiCspSourceExpression = (directive, rawSource, options) => {
    const source = rawSource.trim();
    const invalidSourceCode = getInvalidPlainSourceCode(source);
    if (invalidSourceCode) {
        return { isValid: false, code: invalidSourceCode };
    }
    try {
        return normalizeParsedSourceExpression(directive, new URL(source), options);
    }
    catch {
        return { isValid: false, code: CustomUiCspSourceValidationErrorCode.MalformedUrl };
    }
};
const normalizeDirectiveSources = (directive, sources, options) => {
    const results = sources.map((source) => ({
        source,
        result: normalizeCustomUiCspSourceExpression(directive, source, options),
    }));
    return {
        sources: [...new Set(results.flatMap(({ result }) => (result.isValid ? [result.value] : [])))],
        errors: results.flatMap(({ source, result }) => result.isValid ? [] : [{ directive, source, code: result.code }]),
    };
};
export const normalizeCustomUiCsp = (customUiCsp, options) => customUiCspDirectives.reduce(({ customUiCsp: normalizedCustomUiCsp, errors }, directive) => {
    const sources = customUiCsp[directive];
    if (!sources?.length) {
        return { customUiCsp: normalizedCustomUiCsp, errors };
    }
    const result = normalizeDirectiveSources(directive, sources, options);
    return {
        customUiCsp: result.sources.length > 0
            ? {
                ...normalizedCustomUiCsp,
                [directive]: result.sources,
            }
            : normalizedCustomUiCsp,
        errors: [...errors, ...result.errors],
    };
}, { customUiCsp: {}, errors: [] });
export const hasCustomUiCspSources = (customUiCsp) => Boolean(customUiCsp && customUiCspDirectives.some((directive) => customUiCsp[directive]?.length));
